import { FC } from 'react';
import { Form, useField } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { datadogLogs } from '@datadog/browser-logs';
import { ModalComponent } from 'components/Modal';
import { TextField } from 'components/Forms/TextField';
import { SelectField } from 'components/Forms/SelectField';
import { PhoneAdapter } from 'components/Forms/PhoneField';
import { showToast } from 'components/Toast';
import { required } from 'helpers/utils';
import { getApiHttpStatus } from 'helpers/rtk-utils';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';
import type { PositionCategory } from 'store/admins/types';
import { RecipientAdminRow, useAddRecipientAdminMutation } from 'services/recipientAdminsApi';
import { AddAdminFormValues } from './types';
import { POSITION_CATEGORY_OPTIONS, COPY } from './constants';
import { describeRecipientAdminFailure, isDuplicateEmail } from './helpers';

interface Props {
  isOpen: boolean;
  recipientId: string;
  admins: RecipientAdminRow[];
  onClose: () => void;
}

const SCREEN: DashboardScreen = 'settings_recipient_admins';

const DuplicateEmailWarning: FC<{ admins: RecipientAdminRow[] }> = ({ admins }) => {
  const {
    input: { value },
  } = useField('email', { subscription: { value: true } });

  if (!isDuplicateEmail(value || '', admins)) return null;
  return <span className="oath-bodysmall error">{COPY.duplicateEmailWarning}</span>;
};

export const AddAdminModal: FC<Props> = ({ isOpen, recipientId, admins, onClose }) => {
  const [addRecipientAdmin] = useAddRecipientAdminMutation();

  const handleSubmit = async (values: AddAdminFormValues) => {
    const body = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      phone_number: values.phone_number
        ? values.phone_number.startsWith('+')
          ? values.phone_number
          : `+${values.phone_number}`
        : null,
      position_category: values.position_category as PositionCategory,
    };

    try {
      await addRecipientAdmin({ recipientId, body }).unwrap();
      showToast('Admin added successfully', 'success');
      trackEvent('Recipient Admin Added', { screen: SCREEN });
      onClose();
    } catch (error: unknown) {
      const { message, severity, logMessage, logContext } = describeRecipientAdminFailure(
        error,
        'add',
        'Something went wrong adding this admin. Please try again.'
      );
      // Third argument guarded to a real Error: the SDK stringifies a non-Error into the log
      // message, and the admin API echoes the submitted name, email and phone back on a 422.
      const logDetails = { recipientId, ...logContext };
      if (severity === 'info') {
        datadogLogs.logger.info(logMessage, logDetails);
      } else {
        datadogLogs.logger.error(
          logMessage,
          logDetails,
          error instanceof Error ? error : undefined
        );
      }
      showToast(message, 'error', getApiHttpStatus(error));
      return { [FORM_ERROR]: message };
    }
  };

  return (
    <ModalComponent isOpen={isOpen} closeModal={onClose}>
      <Form<AddAdminFormValues>
        key={String(isOpen)}
        onSubmit={handleSubmit}
        initialValues={{
          first_name: '',
          last_name: '',
          email: '',
          phone_number: '',
          position_category: '',
        }}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit} className="grid-column">
            <span className="oath-eyebrow inkBlue">Add admin</span>
            <TextField label="First Name *" name="first_name" type="text" validate={required} />
            <TextField label="Last Name *" name="last_name" type="text" validate={required} />
            <TextField label="Email *" name="email" type="text" validate={required} />
            <DuplicateEmailWarning admins={admins} />
            <TextField label="Phone Number" name="phone_number" component={PhoneAdapter} />
            <div className="form-input">
              <label htmlFor="position_category">Position Category *</label>
              <SelectField
                name="position_category"
                options={POSITION_CATEGORY_OPTIONS}
                validate={required}
              />
            </div>
            {submitError && <span className="oath-bodysmall error">{submitError}</span>}
            <button type="submit" className="btn-inkBlue btn btn-secondary" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add admin'}
            </button>
            <button
              type="button"
              className="oath-bodysmall link-text inkBlue padding0"
              onClick={onClose}
            >
              Cancel
            </button>
          </form>
        )}
      />
    </ModalComponent>
  );
};
